// frontend/src/pages/admin/PermissionManagement.jsx
// Admin Permission Management Interface

import React, { useState, useEffect } from 'react';
import api from '../../utils/api';

const PermissionManagement = () => {
  const [users, setUsers] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(true);
  
    const [permissionForm, setPermissionForm] = useState({

    freelancer: {
      canSearch: false,
      canImport: false,
      canView: false,
      canUpdate: false,
      canSync: false,
      canDelete: false,
      canAssign: false,
      canViewAnalytics: false
    },
    clients: {
      canCreate: false,
      canView: false,
      canUpdate: false,
      canDelete: false,
      canViewAnalytics: false
    },
    tasks: {
      canCreate: false,
      canView: false,
      canUpdate: false,
      canDelete: false,
      canAssign: false
    },
    leaves: {
      canViewAll: false,
      canApprove: false,
      canReject: false
    },
    analytics: {
      canViewPerformance: false,
      canGenerateReports: false,
      canEmailReports: false
    },
    users: {
      canCreate: false,
      canView: false,
      canUpdate: false,
      canDelete: false
    },
    notes: '',
    expiresAt: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [usersRes, permissionsRes] = await Promise.all([
        api.get('/users'),
        api.get('/permissions')
      ]);
      
        setUsers(usersRes?.data?.data || []);
        setPermissions(permissionsRes?.data?.data || []);
    } catch (error) {
      console.error('Fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditPermissions = async (user) => {
    setSelectedUser(user);
    
    // Fetch user's current permissions
    try {
      const response = await api.get(`/permissions/user/${user._id}`);
      if (response.data.data) {
        setPermissionForm({
          ...response.data.data,
          notes: response.data.data.notes || '',
          expiresAt: response.data.data.expiresAt || ''
        });
      } else {
        // Reset to default
        resetForm();
      }
    } catch (error) {
      console.error('Fetch permissions error:', error);
      resetForm();
    }
    
    setShowModal(true);
  };

  const resetForm = () => {
    setPermissionForm({
      freelancer: {
        canSearch: false,
        canImport: false,
        canView: false,
        canUpdate: false,
        canSync: false,
        canDelete: false,
        canAssign: false,
        canViewAnalytics: false
      },
      clients: {
        canCreate: false,
        canView: false,
        canUpdate: false,
        canDelete: false,
        canViewAnalytics: false
      },
      tasks: {
        canCreate: false,
        canView: false,
        canUpdate: false,
        canDelete: false,
        canAssign: false
      },
      leaves: {
        canViewAll: false,
        canApprove: false,
        canReject: false
      },
      analytics: {
        canViewPerformance: false,
        canGenerateReports: false,
        canEmailReports: false
      },
      users: {
        canCreate: false,
        canView: false,
        canUpdate: false,
        canDelete: false
      },
      notes: '',
      expiresAt: ''
    });
  };

  const handleSavePermissions = async (e) => {
    e.preventDefault();
    
    try {
      await api.post('/permissions', {
        userId: selectedUser._id,
        ...permissionForm
      });
      
      alert('Permissions updated successfully!');
      setShowModal(false);
      setSelectedUser(null);
      fetchData();
    } catch (error) {
      console.error('Save permissions error:', error);
      alert('Failed to save permissions');
    }
  };

  const handleRevokePermissions = async (userId) => {
      if (window.confirm('Are you sure you want to revoke all permissions for this user?'))  {
      return;
    }
    
    try {
      await api.delete(`/permissions/${userId}`);
      alert('Permissions revoked successfully!');
      fetchData();
    } catch (error) {
      console.error('Revoke permissions error:', error);
      alert('Failed to revoke permissions');
    }
  };

  const handleCheckboxChange = (module, permission) => {
    setPermissionForm(prev => ({
      ...prev,
      [module]: {
        ...prev[module],
        [permission]: !prev[module][permission]
      }
    }));
  };

  const selectAll = (module) => {
    const allTrue = {};
    Object.keys(permissionForm[module]).forEach(key => {
      allTrue[key] = true;
    });
    
    setPermissionForm(prev => ({
      ...prev,
      [module]: allTrue
    }));
  };

  const clearAll = (module) => {
    const allFalse = {};
    Object.keys(permissionForm[module]).forEach(key => {
      allFalse[key] = false;
    });
    
    setPermissionForm(prev => ({
      ...prev,
      [module]: allFalse
    }));
  };

  const getUserPermissionCount = (userId) => {
    const perm = permissions.find(p => p.user._id === userId);
    if (!perm) return 0;
    
    let count = 0;
    Object.keys(perm).forEach(key => {
      if (typeof perm[key] === 'object' && !Array.isArray(perm[key])) {
        Object.values(perm[key]).forEach(val => {
          if (val === true) count++;
        });
      }
    });
    return count;
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-800">Permission Management</h1>
        <p className="text-gray-600 mt-1">Grant granular access to users</p>
      </div>

      {/* Users List */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Permissions
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {users.filter(u => u.role?.name?.toLowerCase() !== 'admin' && u.role?.name?.toLowerCase() !== 'super_admin').map(user => (
                <tr key={user._id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center">
                        <span className="text-blue-600 font-semibold">
                          {user.name.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">{user.name}</div>
                        <div className="text-sm text-gray-500">{user.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                      {user.role?.name}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-900">
                      {getUserPermissionCount(user._id)} custom permissions
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                    <button
                      onClick={() => handleEditPermissions(user)}
                      className="text-blue-600 hover:text-blue-900"
                    >
                      Edit Permissions
                    </button>
                    {getUserPermissionCount(user._id) > 0 && (
                      <button
                        onClick={() => handleRevokePermissions(user._id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        Revoke
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Permission Modal */}
      {showModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <div>
                <h2 className="text-2xl font-bold">Manage Permissions</h2>
                <p className="text-gray-600">{selectedUser.name} ({selectedUser.email})</p>
              </div>
              <button
                onClick={() => {
                  setShowModal(false);
                  setSelectedUser(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleSavePermissions}>
              {/* Freelancer Permissions */}
              <PermissionSection
                title="Freelancer Projects"
                module="freelancer"
                permissions={permissionForm.freelancer}
                onChange={handleCheckboxChange}
                selectAll={selectAll}
                clearAll={clearAll}
                labels={{
                  canSearch: 'Search Projects',
                  canImport: 'Import Projects',
                  canView: 'View Projects',
                  canUpdate: 'Update Projects',
                  canSync: 'Sync with API',
                  canDelete: 'Delete Projects',
                  canAssign: 'Assign Projects',
                  canViewAnalytics: 'View Analytics'
                }}
              />

              {/* Client Permissions */}
              <PermissionSection
                title="Client Management"
                module="clients"
                permissions={permissionForm.clients}
                onChange={handleCheckboxChange}
                selectAll={selectAll}
                clearAll={clearAll}
                labels={{
                  canCreate: 'Create Clients',
                  canView: 'View Clients',
                  canUpdate: 'Update Clients',
                  canDelete: 'Delete Clients',
                  canViewAnalytics: 'View Analytics'
                }}
              />

              {/* Task Permissions */}
              <PermissionSection
                title="Task Management"
                module="tasks"
                permissions={permissionForm.tasks}
                onChange={handleCheckboxChange}
                selectAll={selectAll}
                clearAll={clearAll}
                labels={{
                  canCreate: 'Create Tasks',
                  canView: 'View Tasks',
                  canUpdate: 'Update Tasks',
                  canDelete: 'Delete Tasks',
                  canAssign: 'Assign Tasks'
                }}
              />

              {/* Leave Permissions */}
              <PermissionSection
                title="Leave Management"
                module="leaves"
                permissions={permissionForm.leaves}
                onChange={handleCheckboxChange}
                selectAll={selectAll}
                clearAll={clearAll}
                labels={{
                  canViewAll: 'View All Leaves',
                  canApprove: 'Approve Leaves',
                  canReject: 'Reject Leaves'
                }}
              />

              {/* Analytics Permissions */}
              <PermissionSection
                title="Analytics"
                module="analytics"
                permissions={permissionForm.analytics}
                onChange={handleCheckboxChange}
                selectAll={selectAll}
                clearAll={clearAll}
                labels={{
                  canViewPerformance: 'View Performance',
                  canGenerateReports: 'Generate Reports',
                  canEmailReports: 'Email Reports'
                }}
              />

              {/* User Management Permissions */}
              <PermissionSection
                title="User Management"
                module="users"
                permissions={permissionForm.users}
                onChange={handleCheckboxChange}
                selectAll={selectAll}
                clearAll={clearAll}
                labels={{
                  canCreate: 'Create Users',
                  canView: 'View Users',
                  canUpdate: 'Update Users',
                  canDelete: 'Delete Users'
                }}
              />

              {/* Notes */}
              <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notes (Optional)
                </label>
                <textarea
                  value={permissionForm.notes}
                  onChange={(e) => setPermissionForm(prev => ({ ...prev, notes: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  rows="2"
                  placeholder="Add notes about these permissions..."
                />
              </div>

              {/* Expiry Date */}
              <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Expiry Date (Optional)
                </label>
                <input
                  type="date"
                  value={permissionForm.expiresAt ? permissionForm.expiresAt.split('T')[0] : ''}
                  onChange={(e) => setPermissionForm(prev => ({ ...prev, expiresAt: e.target.value }))}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Actions */}
              <div className="flex gap-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setSelectedUser(null);
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700"
                >
                  Save Permissions
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

// Permission Section Component
const PermissionSection = ({ title, module, permissions, onChange, selectAll, clearAll, labels }) => {
  return (
    <div className="mb-6 p-4 bg-gray-50 rounded-lg">
      <div className="flex justify-between items-center mb-3">
        <h3 className="text-lg font-semibold text-gray-800">{title}</h3>
        <div className="space-x-2">
          <button
            type="button"
            onClick={() => selectAll(module)}
            className="text-sm text-blue-600 hover:text-blue-800"
          >
            Select All
          </button>
          <button
            type="button"
            onClick={() => clearAll(module)}
            className="text-sm text-gray-600 hover:text-gray-800"
          >
            Clear All
          </button>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {Object.keys(permissions).map(permission => (
          <label key={permission} className="flex items-center space-x-2 cursor-pointer">
            <input
              type="checkbox"
              checked={permissions[permission]}
              onChange={() => onChange(module, permission)}
              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <span className="text-sm text-gray-700">{labels[permission]}</span>
          </label>
        ))}
      </div>
    </div>
  );
};

export default PermissionManagement;
